import { useState } from 'react';
import { connect } from 'react-redux';
import {
  Options,
  Column,
  PackageContainer,
  Title,
  Description,
  SelectedCheck,
  Icon,
  ModalIcon,
  ModalSubHeaderContainer,
  ModalSubHeaderCharge,
  ModalSubHeaderTitle,
  ModalSubHeaderTitleCont,
  MobileRow,
  UnSelectedCheck,
  Table,
  CoveragesButton,
  PackageContainerProps,
} from './PackagesStyledComponents';
import handleUpdateApplication from '../../../../actions/handleUpdateApplication';
import { setEndorsementPackage } from '../../../../actions/application';
import { selectEndorsements, selectEndorsementPackage, selectPolicyTypes } from '../../../../selectors/application';
import Modal from '../../../../elements/Modal';
import MoreDetailsTable from './MoreDetailsTable';
import { coverages, transformCoveragesForDetails, packages } from './endorsementsData';
import { SectionContainer, SectionDescription, SectionSubHeader } from '../../styles';
import { Desktop } from '../../../../helpers/responsive';
import { coterieIconSrc } from '../../../../elements/theme/themeConstants';
import { useFeatureFlags } from '../../../../toggle_tools/featureFlagTools';
import styled from 'styled-components';
import { GhostButton } from '../../../../elements';

interface PackagesProps {
  selectedPackage?: string;
  setEndorsementPackage: (endorsement: string) => void;
  handleUpdateApplication: () => void;
  applicationTypes: string[];
}

const Packages = ({
  selectedPackage,
  setEndorsementPackage,
  handleUpdateApplication,
  applicationTypes,
}: PackagesProps) => {
  const [modalOption, setModalOption] = useState<string | null>(null);

  const toggleCoveragesModal = (option: string) => {
    setModalOption(option);
  };

  const closeCoveragesModal = () => {
    setModalOption(null);
  };

  const handleSelectPackage = (option: string) => {
    const selection = option === selectedPackage ? 'None' : option;
    setEndorsementPackage(selection);
    handleUpdateApplication();
  };

  type Coverage = {
    option: string;
    flat_premium_charge: string;
    description: string;
  };

  const getModalDetailsBar = (coverage: Coverage) => (
    <>
      <ModalSubHeaderContainer>
        <ModalSubHeaderTitleCont>
          <ModalIcon src={coterieIconSrc[coverage.option]} alt={coverage.option} />
          <ModalSubHeaderTitle>{coverage.option} Coverage Package</ModalSubHeaderTitle>
        </ModalSubHeaderTitleCont>
        <ModalSubHeaderCharge>{coverage.flat_premium_charge}</ModalSubHeaderCharge>
      </ModalSubHeaderContainer>
      <Table>
        <thead>
          <tr>
            <th>COVERAGE</th>
            <th>LIMIT</th>
          </tr>
        </thead>
      </Table>
    </>
  );

  return (
    <SectionContainer data-cy="packages">
      <SectionSubHeader data-cy="packages-subheader">Recommended Coverage Packages</SectionSubHeader>
      <PackagesDescription data-cy="packages-description">
        Select a coverage package to further protect your business. These packages increase some limits within your
        Business Owner Policy.
      </PackagesDescription>
      <Options>
        {packages.map((coverage) => (
          <Column key={coverage.option} data-pendo={`package-card-${coverage.option.toLowerCase().trim()}`}>
            <StylePackageContainer active={selectedPackage === coverage.option}>
              {selectedPackage === coverage.option ? (
                <SelectedCheck
                  onClick={() => handleSelectPackage(coverage.option)}
                  aria-label={`Remove ${coverage.option} coverage from plan`}
                />
              ) : (
                <UnSelectedCheck
                  onClick={() => handleSelectPackage(coverage.option)}
                  aria-label={`Add ${coverage.option} coverage to plan`}
                />
              )}
              <MobileRow>
                <Icon src={coterieIconSrc[coverage.option]} alt={coverage.option} />
                <div>
                  <Title>{coverage.option}</Title>
                  <Description>{coverage.description}</Description>
                </div>
              </MobileRow>
              <div>
                <SelectPlanBtn
                  onClick={() => handleSelectPackage(coverage.option)}
                  disabled={selectedPackage === coverage.option}
                  data-cy={coverage.option}
                  type="button"
                  aria-label={`Add ${coverage.option} coverage to plan`}
                  full
                  small
                >
                  {selectedPackage === coverage.option ? (
                    'Selected'
                  ) : (
                    <>
                      Select <Desktop>Plan</Desktop>
                    </>
                  )}
                </SelectPlanBtn>
                <CoveragesButton
                  onClick={() => toggleCoveragesModal(coverage.option)}
                  aria-label={`See ${coverage.option} coverages`}
                >
                  Coverages
                </CoveragesButton>
              </div>
            </StylePackageContainer>
            <Modal
              isOpen={modalOption === coverage.option}
              onRequestClose={closeCoveragesModal}
              label={`more details on ${coverage.option} endorsement modal`}
              title="Coverages"
              detailsBar={getModalDetailsBar(coverage)}
              options={{ noContainerPadding: true }}
            >
              <MoreDetailsTable rows={transformCoveragesForDetails(coverages, coverage.option)} hideTableHeader />
            </Modal>
          </Column>
        ))}
      </Options>
    </SectionContainer>
  );
};

const PackagesDescription = styled(SectionDescription)`
  color: ${({ theme }) => theme.colors.neutral.dark};
  font-family: Figtree;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`;

const StylePackageContainer = styled(PackageContainer)`
  background: ${({ active, theme }: PackageContainerProps) =>
    active ? theme.colors.neutral.special : theme.colors.neutral.white};
  border: 1px solid #d4d4dd;
  @media (max-width: 767px) {
    max-width: 294px;
    margin: 0px auto 15px;
  }
`;

const SelectPlanBtn = styled(GhostButton)`
  margin: 0 auto;
  min-width: 121px;
  @media (max-width: 767px) {
    min-width: 214px;
  }
`;

const mapStateToProps = (state: ReduxState) => ({
  endorsements: selectEndorsements(state),
  selectedPackage: selectEndorsementPackage(state),
  applicationTypes: selectPolicyTypes(state),
});

const mapDispatchToProps = { setEndorsementPackage, handleUpdateApplication };

export default connect(mapStateToProps, mapDispatchToProps)(Packages);
